/* =============================================================================
 * Copyright (c) 2026 SKYXOPS Corp. All rights reserved.
 * ============================================================================= */
/**
 * <AppCommandPalette> (console-pro-upgrade B-common / R1) — mounts the shared
 * <CommandPalette> once in the launcher's authed shell. ⌘K / Ctrl-K (via
 * useCommandPalette) toggles it.
 *
 * Commands:
 *   1. Apps — one command per entitled tile. Selecting one navigates the SAME
 *      way clicking the tile does: a full-page load to `buildTileHref(route_prefix)`,
 *      so the cross-subdomain cookie / dev `?token=` hand-off is preserved. (Tiles
 *      point at other origins, so this is a `window.location` navigation, NOT a
 *      react-router push.)
 *   2. Back to Launcher — an in-app `navigate('/')` to the tile grid. Useful from
 *      /profile; uses react-router (not the cross-origin BackToLauncherButton URL,
 *      which from the launcher would just point launchpad → launchpad).
 *
 * Tiles live only in <TileGridPage>; rather than lift that page's data flow into
 * App (the design preserves it verbatim), this component fetches its own copy via
 * getTiles() — one extra cheap GET. The tile per-app icon/accent metadata is the
 * same `tileMeta()` the grid uses, so palette icons match the tiles.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Home } from 'lucide-react';
import { CommandPalette, useCommandPalette } from '@skyxops/ui';
import type { CommandGroup, CommandItem } from '@skyxops/ui';
import { getTiles, Tile } from '../api';
import { buildTileHref } from './tileHref';
import { tileMeta } from './tileMeta';

export function AppCommandPalette() {
  const navigate = useNavigate();
  const intl = useIntl();
  const { open, setOpen } = useCommandPalette();
  const [tiles, setTiles] = useState<Tile[]>([]);

  // Fetch the entitled tiles once for the palette. Failures are swallowed — the
  // palette still offers Back-to-Launcher; the tile grid surfaces real errors.
  useEffect(() => {
    let cancelled = false;
    getTiles()
      .then((r) => {
        if (!cancelled) setTiles(r.tiles);
      })
      .catch(() => {
        /* keep palette usable with the Back-to-Launcher command only */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const groups: CommandGroup[] = useMemo(() => {
    const appItems: CommandItem[] = tiles.map((t) => {
      const { icon } = tileMeta(t);
      return {
        id: `tile:${t.app_id}`,
        label: t.name,
        keywords: t.route_prefix,
        icon,
        // Same hand-off as clicking the tile: full-page nav through buildTileHref
        // so the dev ?token= / prod cookie reaches the target app's origin.
        onSelect: () => {
          window.location.href = buildTileHref(t.route_prefix);
        },
      };
    });

    const result: CommandGroup[] = [];
    if (appItems.length > 0) {
      result.push({
        heading: intl.formatMessage({ id: 'palette.apps_heading' }),
        items: appItems,
      });
    }
    result.push({
      heading: intl.formatMessage({ id: 'palette.actions_heading' }),
      items: [
        {
          id: 'nav:launcher-home',
          label: intl.formatMessage({ id: 'header.back_to_launcher' }),
          icon: Home,
          onSelect: () => navigate('/'),
        },
      ],
    });
    return result;
  }, [tiles, intl, navigate]);

  return (
    <CommandPalette
      open={open}
      onOpenChange={setOpen}
      groups={groups}
      placeholder={intl.formatMessage({ id: 'palette.placeholder' })}
    />
  );
}
